import { useEffect } from 'react';

import { UnauthorizedError } from '@/lib/api/errors';
import { sessionExpiry } from '@/lib/api/session-expiry';

import { renewSession } from './api';
import { AUTH_POLICY } from './constants';

/**
 * Keeps a live session's refresh cookie alive.
 *
 * The cookie outlives the access token by only five minutes (see `AUTH_POLICY.keepAliveMs`),
 * so the response interceptor's reactive refresh — which only ever runs when something 401s —
 * cannot preserve a session that simply isn't making requests. This is the proactive half.
 *
 * A 401 here means the cookie is already gone, so the session is unrecoverable and is ended
 * the same way a failed reactive refresh ends it. Every other failure (offline, a blip) is
 * swallowed: the next tick tries again, and the interceptor still catches a genuinely dead
 * session on the admin's next real request. The refresh endpoint is an auth-entry path, so
 * this call is never itself refresh-retried.
 */
export function useSessionKeepAlive({ enabled }: { enabled: boolean }): void {
  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => {
      void renewSession().catch((error: unknown) => {
        if (error instanceof UnauthorizedError)
          sessionExpiry.expire({ code: error.code, message: error.message });
      });
    }, AUTH_POLICY.keepAliveMs);
    return () => window.clearInterval(id);
  }, [enabled]);
}
